import ProductPreview from '@/components/ProductPreview'
import { useTranslate } from '@/hooks'
import { Button, ReverLoading } from '@itsrever/design-system'
import type {
    ReturnsExchangeOptionsResponse,
    ReturnsExchangeOptionsResponseOneOnOneVariantInfo,
    ReturnsLineItem
} from '@itsrever/rever-api'
import { useTranslation } from 'react-i18next'

interface InitialWithSizeExchangesProps {
    product: ReturnsLineItem
    variants: ReturnsExchangeOptionsResponseOneOnOneVariantInfo[]
    selectedVariantId: string
    selectedQuantity: number
    setSelectedVariantId: (variant: string) => void
    handleContinueExchange: () => void
    handleClickFullCatalog: () => void
    handleClickRefund: () => void
    loading: boolean
    isFullCatalog: boolean
    exchangeOptions: ReturnsExchangeOptionsResponse
}

export const InitialWithSizeExchanges = ({
    product,
    variants,
    selectedVariantId,
    setSelectedVariantId,
    handleContinueExchange,
    handleClickFullCatalog,
    handleClickRefund,
    loading,
    exchangeOptions,
    selectedQuantity,
    isFullCatalog
}: InitialWithSizeExchangesProps) => {
    const { translate } = useTranslate()
    const { t } = useTranslation()

    const isAvailableForExchange =
        (exchangeOptions?.exchange_options?.exchange_flow ===
            'EXCHANGE_FLOW_OPEN' ||
            exchangeOptions.exchange_options?.exchange_flow ===
                'EXCHANGE_FLOW_OPEN_WITH_GIFT_CARD' ||
            exchangeOptions?.line_item_product_info?.variants?.some(
                (variant) => {
                    return (
                        variant.is_available &&
                        (!variant.is_inventory_enabled ||
                            (variant.inventory_quantity !== undefined &&
                                variant.inventory_quantity > 0))
                    )
                }
            )) ??
        false

    const shouldRefund =
        exchangeOptions.exchange_options?.force_exchange !== true

    const hasStock = (
        variant: ReturnsExchangeOptionsResponseOneOnOneVariantInfo
    ) => {
        return (
            !variant.is_inventory_enabled ||
            (variant.inventory_quantity &&
                variant.inventory_quantity >= 1 &&
                selectedQuantity <= variant.inventory_quantity)
        )
    }

    if (loading)
        return (
            <div className="flex h-fit w-full items-center justify-center py-10">
                <ReverLoading />
            </div>
        )

    return (
        <div className="h-fit w-full min-w-[250px] px-3 pb-6 pt-3 md:w-96 md:pb-4">
            <div className="space-y-4">
                <div
                    id="productPreview"
                    className="border-b border-solid border-neutral-300 pb-4"
                >
                    <ProductPreview product={product} />
                </div>
                <div>
                    <p
                        className="text-center text-lg font-semibold"
                        dangerouslySetInnerHTML={{
                            __html: translate('item_modal_moderate_title')
                        }}
                    />
                    <div className="flex flex-col items-center pt-3">
                        {isAvailableForExchange ? (
                            <>
                                <div className="flex w-full flex-col items-center gap-4">
                                    <p
                                        className="text-center"
                                        dangerouslySetInnerHTML={{
                                            __html: translate(
                                                'select_exchange_title'
                                            )
                                        }}
                                    />
                                    <div className="w-full pb-2">
                                        <ul className="flex max-h-[17rem] flex-col items-center gap-2 overflow-y-auto">
                                            {variants.map((variant, i) => (
                                                <li
                                                    id={`variant-${i}`}
                                                    data-selected={
                                                        selectedVariantId ===
                                                        variant.platform_id
                                                    }
                                                    data-stock={
                                                        !!hasStock(variant)
                                                    }
                                                    onClick={() =>
                                                        setSelectedVariantId(
                                                            variant.platform_id ??
                                                                ''
                                                        )
                                                    }
                                                    key={variant.platform_id}
                                                    className="data-[stock:true]:border-neutral-400 pointer-events-none flex w-full min-w-40 max-w-fit grow-0 items-center justify-center rounded-md border border-solid border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-400 hover:bg-neutral-100 data-[stock=true]:pointer-events-auto data-[stock=true]:cursor-pointer data-[stock=true]:border-neutral-400 data-[selected=true]:bg-neutral-200 data-[stock=true]:text-neutral-800"
                                                >
                                                    {variant.name}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="w-full max-w-36">
                                        <Button
                                            id="continueExchange"
                                            disabled={selectedVariantId === ''}
                                            onClick={handleContinueExchange}
                                            fullWidth
                                        >
                                            <p
                                                dangerouslySetInnerHTML={{
                                                    __html: translate(
                                                        'select_exchange_button_continue'
                                                    )
                                                }}
                                            />
                                        </Button>
                                    </div>
                                </div>
                                <div className="w-fit pt-5">
                                    <p className="relative text-sm font-normal text-neutral-400 before:absolute before:-left-3 before:top-1/2 before:block before:w-24 before:-translate-x-full before:translate-y-1/2 before:border-[0.5px] before:border-solid before:border-neutral-300 after:absolute after:-right-3 after:top-1/2 after:w-24 after:translate-x-full after:border-[0.5px] after:border-solid after:border-neutral-300">
                                        {t('initial_selection.or')}
                                    </p>
                                </div>
                            </>
                        ) : null}
                        <div className="flex w-full flex-col items-center justify-center gap-5 pt-5">
                            <div className="flex flex-col justify-between gap-5">
                                {isFullCatalog ? (
                                    <Button
                                        onClick={handleClickFullCatalog}
                                        id="exploreButton"
                                    >
                                        <p
                                            dangerouslySetInnerHTML={{
                                                __html: translate(
                                                    'change_product_full_catalog'
                                                )
                                            }}
                                        />
                                    </Button>
                                ) : null}
                                {shouldRefund ? (
                                    <button
                                        type="button"
                                        className="cursor-pointer text-neutral-700 underline underline-offset-4"
                                        onClick={handleClickRefund}
                                        dangerouslySetInnerHTML={{
                                            __html: translate(
                                                'item_modal_moderate_refund'
                                            )
                                        }}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
